import { useEffect, useState } from "react";
import { X, Folder } from "lucide-react";

/**
 * ProjectModal — create or edit a Project. When `project` is null it's
 * create mode; otherwise it's edit mode. Custom instructions are
 * optional and applied to every chat inside the project.
 */
export default function ProjectModal({ open, project, onClose, onSave, busy = false }) {
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const isEdit = !!project;

  useEffect(() => {
    if (!open) return;
    setName(project?.name || "");
    setInstructions(project?.custom_instructions || "");
  }, [open, project]);

  if (!open) return null;

  const canSave = name.trim().length > 0 && !busy;

  return (
    <div
      className="fixed inset-0 z-[90] bg-[var(--text)]/70 flex items-center justify-center px-4"
      onClick={busy ? undefined : onClose}
      data-testid="project-modal-backdrop"
    >
      <div
        className="max-w-lg w-full bg-[var(--bg-elevated)] border border-[var(--border)] rounded-lg p-6 text-[var(--text)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="project-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Folder size={16} className="text-[var(--text)]/80" />
            <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text)]/60">
              {isEdit ? "Edit project" : "New project"}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="text-[var(--text)]/60 hover:text-[var(--text)] transition-colors"
            data-testid="project-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <label className="block mb-4">
          <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]/60 mb-2">
            Name
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            placeholder="e.g. Career decisions"
            maxLength={80}
            data-testid="project-name-input"
            className="w-full px-3 py-2 bg-transparent border border-[var(--border)] focus:border-[var(--border)] outline-none font-ui text-base text-[var(--text)] placeholder:text-[var(--text)]/40 transition-colors"
            autoFocus
          />
        </label>

        <label className="block mb-6">
          <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]/60 mb-2">
            Custom instructions <span className="normal-case tracking-normal text-[var(--text)]/40">(optional — applied to every chat in this project)</span>
          </div>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value.slice(0, 4000))}
            placeholder="e.g. Always answer in the context of a first-time founder. Reference the Indian startup ecosystem where relevant. Be blunt, no corporate hedging."
            rows={7}
            maxLength={4000}
            data-testid="project-instructions-input"
            className="w-full px-3 py-2 bg-transparent border border-[var(--border)] focus:border-[var(--border)] outline-none font-ui text-sm text-[var(--text)] placeholder:text-[var(--text)]/40 transition-colors resize-none leading-relaxed"
          />
          <div className="text-right font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text)]/40 mt-1">
            {instructions.length} / 4000
          </div>
        </label>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-2.5 border border-[var(--border)] hover:bg-[var(--text)]/10 font-mono-ui text-[10px] tracking-[0.24em] uppercase transition-colors disabled:opacity-50"
            data-testid="project-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => canSave && onSave({ name: name.trim(), custom_instructions: instructions.trim() })}
            disabled={!canSave}
            className="flex-1 py-2.5 bg-[var(--accent)] text-white hover:opacity-90 font-mono-ui text-[10px] tracking-[0.24em] uppercase transition-colors disabled:opacity-50"
            data-testid="project-modal-save"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create project"}
          </button>
        </div>
      </div>
    </div>
  );
}
